import React, { FunctionComponent, useMemo } from 'react';
import { Typography, IconButton, useMediaQuery, Link } from '@rbx/ui';
import { Locale, useTranslation } from '@rbx/intl';
import { useRouter } from 'next/router';
import { SvgIconTableOfContent } from '@modules/ui/icons/EngineReferenceIcons';
import { ApiTag, EngineTag } from '@modules/ui/tags/types';
import { TCompileTree } from '@modules/markdownProcessor/markdownProcessor';
import { ClassReferenceParent, ReferenceType } from '@modules/client';
import EngineMarkdown from '@modules/ui/markdown/components/EngineMarkdown';
import { rightNavMinBreakpoint } from '@modules/layout/layout/constants/layoutConstants';
import { EngineReferenceStudioIcon } from '@modules/ui/icons/EngineReferenceStudioIcon';
import { addLeadingSlash } from '@modules/utilities/utils/path';
import { getLocalePrefix } from '@modules/localization/implementations/translationResourceHelpers';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { splitDeprecatedTags } from '../utils/tagUtils';
import ReferenceMemberTags from './ReferenceMemberTags';
import useReferenceHeadingStyles from './ReferenceHeading.styles';
import ReferenceDivider from './ReferenceDivider';
import ReferenceStickyBreadcrumb from './ReferenceStickyBreadcrumb';
import ReferenceDeprecationWarning from './ReferenceDeprecationWarning';

export interface ReferenceHeadingProps {
  title?: string;
  summary?: string | TCompileTree | null;
  tags?: EngineTag[] | null;
  referenceType?: string;
  showSummary?: boolean;
  deprecationMessage?: string | null;
  currentType?: ReferenceType;
  classReferenceParents?: ClassReferenceParent[] | null;
  onTocClick?: () => void;
  hasRightNavItems: boolean;
}

/**
 * Reference page heading component that displays the title, summary, tags, and TOC button.
 * Also renders the sticky breadcrumb component above the main heading content.
 */
const ReferenceHeading: FunctionComponent<ReferenceHeadingProps> = ({
  title,
  summary,
  tags,
  referenceType,
  showSummary = true,
  currentType,
  classReferenceParents,
  onTocClick,
  deprecationMessage,
  hasRightNavItems,
}) => {
  const { classes } = useReferenceHeadingStyles();
  const { translate, translateHTML } = useTranslation();
  const showTocButton = useMediaQuery((theme) => theme.breakpoints.up(rightNavMinBreakpoint));
  const router = useRouter();
  const { machineTranslated } = useAppConfig();

  const englishUrl = useMemo(() => {
    const pathPrefix = [router.basePath, getLocalePrefix(Locale.English)].filter(Boolean).join('/');
    const pathname = router.asPath.replace(/\/\w{2}-\w{2}\//, '/');
    return `${addLeadingSlash(pathPrefix)}${pathname}`;
  }, [router.asPath, router.basePath]);

  const { hasDeprecatedTag: hasDeprecatedTags, nonDeprecatedTags } = useMemo(
    () => splitDeprecatedTags(tags),
    [tags],
  );

  const isDeprecated = hasDeprecatedTags || !!deprecationMessage;

  return (
    <React.Fragment>
      {title && currentType && (
        <ReferenceStickyBreadcrumb
          currentName={title}
          currentType={currentType}
          classReferenceParents={classReferenceParents ?? null}
          onTocClick={onTocClick}
        />
      )}
      <div className={classes.container}>
        <div className={classes.headerRow}>
          {referenceType && (
            <Typography variant='body2' component='span' className={classes.referenceType}>
              {referenceType}
            </Typography>
          )}
          {showTocButton && currentType !== ReferenceType.ENUM && hasRightNavItems && (
            // On large screens, the action buttons are in the breadcrumb,
            // so the TOC button doesn't do anything on enum pages.
            <IconButton
              id='reference-heading-toc-button'
              onClick={onTocClick}
              aria-label={translate('Label.TableOfContents') || 'Table of Contents'}
              size='small'
              className={classes.tocIcon}>
              <SvgIconTableOfContent />
            </IconButton>
          )}
        </div>

        {title && (
          <div className={classes.sectionSpacing}>
            <div className={classes.titleContainer}>
              {currentType === ReferenceType.CLASS && (
                <EngineReferenceStudioIcon
                  name={title}
                  referenceType={currentType}
                  showFallbackIcon={false}
                />
              )}
              <Typography variant='h1'>{title}</Typography>
              {isDeprecated && (
                <ReferenceMemberTags
                  tags={[ApiTag.Deprecated]}
                  memberName={title}
                  engineRevampEnabled
                />
              )}
            </div>
          </div>
        )}

        {showSummary && summary && (
          <div className={classes.sectionSpacing}>
            <EngineMarkdown overrideParagraphStyle={{ marginBottom: '0' }}>
              {summary}
            </EngineMarkdown>
          </div>
        )}

        {nonDeprecatedTags.length > 0 && (
          <div className={classes.sectionSpacing}>
            <ReferenceMemberTags tags={nonDeprecatedTags} memberName={title} engineRevampEnabled />
          </div>
        )}

        {deprecationMessage && (
          <div className={classes.sectionSpacing}>
            <ReferenceDeprecationWarning deprecationMessage={deprecationMessage} />
          </div>
        )}

        {machineTranslated && (
          <div className={classes.sectionSpacing}>
            <Typography variant='body1' component='p' color='disabled'>
              *
              <span>
                {translateHTML('Description.BetaMachineTranslations', [
                  {
                    opening: 'useEnglishLinkStart',
                    closing: 'useEnglishLinkEnd',
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content: (chunk) => (
                      <Link
                        sx={{
                          fontWeight: 'inherit !important',
                          textUnderlineOffset: '2px !important',
                        }}
                        color='inherit'
                        underline='always'
                        // eslint-disable-next-line react/forbid-component-props
                        href={englishUrl}>
                        {chunk}
                      </Link>
                    ),
                  },
                ])}
              </span>
            </Typography>
          </div>
        )}

        <ReferenceDivider />
      </div>
    </React.Fragment>
  );
};

export default ReferenceHeading;
